import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'

import type { Campaign as SegmentCampaign } from '@segment/analytics-core'
import { AnalyticsBrowser } from '@segment/analytics-next'

import { RuntimeConfigs } from '../../../configs/runtime.js'
import { extractUtmDataFromCookies } from '../utils/utm-cookies.utils.js'

const analytics = new AnalyticsBrowser()

const enabled = !!RuntimeConfigs.SEGMENT_WRITE_KEY
const ENVIRONMENT = 'Workspace'

/**
 * Add UTM tracker middleware
 *
 * analytics is a global object. Similarly, middlewares should also be
 * setup globally and outside any framework specific component or lifecycle event.
 * Or we risk registering the same middleware multiple times. Had analytics provided
 * an API to deregister middlewares, the logic could be move into the component and handled
 * as part of an effect clean up function.
 */
analytics.addSourceMiddleware(({ payload, next }) => {
  const utmData = extractUtmDataFromCookies()
  const segmentCampaign: SegmentCampaign = {
    name: utmData.utm_campaign,
    medium: utmData.utm_medium,
    source: utmData.utm_source,
    term: utmData.utm_term,
    content: utmData.utm_content,
  }
  payload.obj.context = {
    ...payload.obj.context,
    campaign: {
      ...segmentCampaign,
      ...payload.obj.context?.campaign,
    },
  }
  if (payload.obj.type === 'identify') {
    payload.obj.traits = {
      ...utmData,
      ...payload.obj.traits,
    }
  } else if (payload.obj.properties !== undefined) {
    payload.obj.properties = {
      environment: ENVIRONMENT,
      ...utmData,
      ...payload.obj.properties,
    }
  }

  next(payload)
})

interface SegmentIdentifyProps {
  name: string
  email: string
}

type SegmentContextType = {
  analytics: AnalyticsBrowser

  /**
   * Whether the module has been loaded.
   * `analytics.initialized` cannot be used, since that will only be set once the library finishes loading,
   * while `loaded` flag ensures the load method get called only once.
   */
  loaded: boolean

  identify?: (props: SegmentIdentifyProps) => Promise<void>
  track?: (event: string, props: unknown) => Promise<void>
}
const singletonContextValue: SegmentContextType = {
  analytics,
  loaded: false,
}

const loadAnalytics = () => {
  if (singletonContextValue.loaded) {
    return
  }

  singletonContextValue.loaded = true

  try {
    analytics.load(
      {
        writeKey: RuntimeConfigs.SEGMENT_WRITE_KEY,
      },
      {
        disable: !enabled,
        initialPageview: false,
      },
    )
  } catch (_) {
    // no-op
  }
}

const SegmentContext = createContext<SegmentContextType>(singletonContextValue)

interface Props {
  children: ReactNode
}

export const SegmentProvider = ({ children }: Props) => {
  const providerInitialized = useRef(false)

  useEffect(() => {
    if (providerInitialized.current) {
      return
    }

    providerInitialized.current = true

    analytics
      .ready(() => {
        singletonContextValue.loaded = true
      })
      .catch(e => {
        logger.error('Failed to load Segment Analytics', e)
      })
  }, [])

  const identify = useCallback(async (props: SegmentIdentifyProps) => {
    const { email } = props
    if (!email) {
      return
    }

    try {
      await analytics.identify(email, {
        ...props,
      })
    } catch (_) {
      // no-op
    }
  }, [])

  const track = useCallback(async <T = object,>(event: string, props: T) => {
    try {
      await analytics.track(event, {
        ...(props ?? {}),
      })
    } catch (_) {
      // no-op
    }
  }, [])

  const existingContext = useContext(SegmentContext)

  const contextValue = useMemo(() => {
    if (existingContext) {
      return { ...existingContext, track, identify }
    }

    return { ...singletonContextValue, track, identify }
  }, [existingContext, identify, track])

  if (existingContext) {
    return (
      <SegmentContext.Provider value={contextValue}>
        {children}
      </SegmentContext.Provider>
    )
  }

  return (
    <SegmentContext.Provider value={contextValue}>
      {children}
    </SegmentContext.Provider>
  )
}

export const useSegment = () => {
  const context = useContext(SegmentContext)
  if (!context) {
    throw new Error('useSegment must be used within a SegmentProvider')
  }

  useEffect(() => {
    if (!context.loaded) {
      loadAnalytics()
    }
  })

  return context
}
